import {computed, inject, Injectable, signal} from "@angular/core";
import {ApiService} from "src/app/services/api.service";
import {HeaderService} from "src/app/services/header.service";
import {UserService} from "src/app/services/user.service";
import {Solution} from "src/app/shared/interfaces/solution";
import {
  BehaviorSubject,
  combineLatest,
  filter,
  forkJoin,
  from,
  map,
  Observable,
  Subject,
  switchMap,
  tap,
} from "rxjs";
import {DomSanitizer} from "@angular/platform-browser";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {MediaFile, SolutionMedia} from "src/app/shared/interfaces/media";

interface SolutionState {
  selectedSolution: Solution;
  mediaFiles: SolutionMedia[];
  selectedMediaFile: SolutionMedia;
  mediaItemBeingEdited: SolutionMedia | null;
}

@Injectable({
  providedIn: "root",
})
export class SolutionService {
  apiService = inject(ApiService);
  userService = inject(UserService);
  headerService = inject(HeaderService);
  sanitizer = inject(DomSanitizer);

  public readonly NO_SOLUTION_AVAILABLE = "Für diese Störung wurde noch kein Lösungsvorgang dokumentiert. Bitte" +
    " erstellen" +
    " Sie eine neue Lösung.";

  editSolution = signal<boolean>(false);
  isSearchResult = signal<boolean>(false);
  searchPath = signal<string>('');

  // sources
  public selectedSolutionId$ = new BehaviorSubject<string | null>(null);
  selectedSolution$: Observable<{
    solution: Solution;
    mediaFiles: SolutionMedia[];
  }> = this.selectedSolutionId$.pipe(
    filter((solutionId): solutionId is string => solutionId !== null),
    switchMap((solutionId: string) => {
      return combineLatest([
        this.getSolutionById(solutionId),
        this.getMediaFilesForSolution(solutionId),
      ]).pipe(
        map(([solution, mediaFiles]) => ({
          solution,
          mediaFiles,
        }))
      );
    })
  );
  selectedSolutionMediaItem$ = new Subject<SolutionMedia>();
  deleteMediaItem$ = new Subject<SolutionMedia>();
  updateMediaItem$ = new Subject<string>();
  mediaItemBeingEdited$ = new Subject<SolutionMedia | null>();

  private initialState = {
    selectedSolution: {
      id: "",
      created_at: "",
      created_by: "",
      name: "",
      description: "",
      malfunction: "",
    },
    mediaFiles: [],
    selectedMediaFile: {
      data: undefined,
      url: undefined,
      type: undefined,
      name: undefined,
      path: undefined,
    },
    mediaItemBeingEdited: null,
  }

  // state
  private state = signal<SolutionState>(this.initialState)


  // selectors
  solution = computed(() => this.state().selectedSolution);
  mediaFiles = computed(() => this.state().mediaFiles);
  selectedMediaFile = computed(() => this.state().selectedMediaFile);
  mediaFileBeingEdited = computed(() => this.state().mediaItemBeingEdited);

  constructor() {
    this.selectedSolutionId$.subscribe();
    this.selectedSolution$.subscribe(({solution, mediaFiles}) => {
      this.state.update((state) => ({
        ...state,
        selectedSolution: solution,
        mediaFiles: mediaFiles,
      }));
    });
    this.selectedSolutionMediaItem$.subscribe((mediaItem) =>
      this.state.update((state) => ({
        ...state,
        selectedMediaFile: mediaItem,
      }))
    );
    this.updateMediaItem$
      .pipe(
        switchMap((filename) =>
          this.apiService.updateMediaFilename(
            filename,
            this.mediaFileBeingEdited()?.path
          )
        ),
        switchMap(() => this.refreshMediaFiles())
      )
      .subscribe();
    this.deleteMediaItem$
      .pipe(
        switchMap((item) =>
          forkJoin([
            this.apiService.deleteMediaItem(item.path),
            this.apiService.deleteStorageItem(item.path),
          ]).pipe(map(() => this.solution().id))
        ),
        switchMap(() => this.refreshMediaFiles()),
        takeUntilDestroyed()
      )
      .subscribe();
    this.mediaItemBeingEdited$.subscribe((mediaItem: SolutionMedia | null) =>
      this.state.update((state) => ({
        ...state,
        mediaItemBeingEdited: mediaItem,
      }))
    );
  }

  getSolutionById(solutionId: string): Observable<Solution> {
    return from(this.apiService.getSolutionByIdAsObservable(solutionId)).pipe(
      map((solution) => solution ?? [])
    );
  }

  getMediaFilesForSolution(solutionId: string): Observable<SolutionMedia[]> {
    return from(
      this.apiService.getMediaFilesForSolution(solutionId).pipe(
        map((mediaFiles: MediaFile[]) => mediaFiles ?? []),
        map((mediaFiles: MediaFile[]) =>
          mediaFiles.map((mediaFile: MediaFile) => {
            return {
              data: mediaFile.file,
              url: mediaFile.file.type.includes("application/pdf")
                ? this.sanitizer.bypassSecurityTrustResourceUrl(
                  URL.createObjectURL(mediaFile.file)
                )
                : this.sanitizer.bypassSecurityTrustUrl(
                  URL.createObjectURL(mediaFile.file)
                ),
              type: mediaFile.file.type,
              name: mediaFile.name,
              path: mediaFile.path,
            };
          })
        )
      )
    );
  }

  refreshMediaFiles() {
    return this.getMediaFilesForSolution(this.solution().id).pipe(
      tap((mediaFiles) => this.selectedSolutionId$.next(this.solution().id)),
      map(() => void 0)
    );
  }

  updateSolution(
    newName: string,
    newDescription: string,
    newPrevention: string,
    newAdditionalInformation: string
  ) {
    this.state.update((state) => ({
      ...state,
      selectedSolution: {
        ...state.selectedSolution,
        name: newName,
        description: newDescription,
        prevention: newPrevention,
        additional_information: newAdditionalInformation,
      },
    }));
  }

  resetState() {
    this.state.set(this.initialState);
  }
}
